/**
 * How short a name is allowed to be.
 *
 * Checkout and signup both ask for a first and a last name, and both used to
 * accept a single character in each. "x x" is not a name, and an order placed
 * under one costs real time to chase down before it can be shipped.
 *
 * The minimum is deliberately low. It is here to stop filler, not to police
 * what a name may be — a threshold high enough to feel safe rejects a great
 * many real people (Ann, Amy, Lee, Ivy, Kim, Max, Ali, Zoe), and even at
 * three it turns away genuine two-letter surnames like Li, Wu and Ng. That
 * trade was made deliberately; MIN_NAME_LENGTH is the one place to revisit it.
 *
 * Deliberately dependency-free so the worker routes and the storefront forms
 * import the same rule, and the message a customer reads in the form is the
 * message the API would have given them.
 */

export const MIN_NAME_LENGTH = 3

/**
 * Length in characters a person would count.
 *
 * Spread rather than `.length` so an accented or non-Latin name is measured
 * in characters rather than UTF-16 code units — "José" is four either way,
 * but a name outside the basic plane would otherwise measure double and slip
 * through a rule it should not.
 */
export function nameLength(value) {
  return [...String(value ?? '').trim()].length
}

/**
 * Validates one name part.
 *
 * `label` is used verbatim in the message, so the caller decides whether the
 * customer reads "First name" or "Last name" — the rule stays in one place
 * while the wording stays specific to the field the cursor is in.
 */
export function validateName(raw, label, { minLength = MIN_NAME_LENGTH } = {}) {
  const value = String(raw ?? '').trim()
  if (!value) return { ok: false, value: '', error: `${label} is required.` }
  if (nameLength(value) < minLength) {
    return { ok: false, value, error: `${label} must be at least ${minLength} characters.` }
  }
  return { ok: true, value }
}

/**
 * Validates a first/last pair, reporting the first problem found.
 *
 * Returns `field` alongside the message so a form can attach the error to
 * the input it belongs to instead of dropping it all in one banner.
 */
export function validateFullName({ firstName, lastName }, opts = {}) {
  const first = validateName(firstName, 'First name', opts)
  if (!first.ok) return { ok: false, field: 'firstName', error: first.error }
  const last = validateName(lastName, 'Last name', opts)
  if (!last.ok) return { ok: false, field: 'lastName', error: last.error }
  return {
    ok: true,
    firstName: first.value,
    lastName:  last.value,
    // The composed name every existing consumer already reads — invoices,
    // emails, shipping labels, the admin list.
    name: `${first.value} ${last.value}`,
  }
}
